import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { MessageRow } from '@st/contract';
import {
  isOptimistic,
  useChatMessages,
  useMarkConversationRead,
  useSendChatMessage,
} from '../../api/queries';
import { useAuth } from '../../auth/AuthProvider';
import { useSocketFactory } from '../../app/socket';
import { useConversationSocket } from '../../hooks/useConversationSocket';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { API_URL } from '../../env';
import { formatTime } from '../../lib/format';
import { cx } from '../../lib/cx';
import { Button, MessageBubble, SkeletonList, useToast } from '../../ui';
import { IconChevronLeft } from '../../components/icons';

/** Idempotencijski ključ mora biti UUID — `POST /messages` ga tako validira. */
function newIdempotencyKey(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

export interface ConversationThreadProps {
  conversationId: string;
  partnerName: string;
  /** Ruta popisa — mobilni „natrag” vodi natrag na cijeli popis. */
  listPath: string;
}

/**
 * Prozor razgovora.
 *
 * Uživo dio ide kroz `useConversationSocket`: pristigle poruke se UPISUJU u
 * cache, bez ponovnog dohvata. Kad veza padne, ekran prelazi na povlačenje
 * svakih 15 s — degradira, ne bijeli se. Slanje uvijek ide REST-om.
 */
export function ConversationThread({
  conversationId,
  partnerName,
  listPath,
}: ConversationThreadProps) {
  const { t, locale } = useTranslation();
  const { user, getAccessToken } = useAuth();
  const socketFactory = useSocketFactory();
  const { toast } = useToast();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const socketUrl = useMemo(() => {
    const token = getAccessToken();
    if (!token) return null;
    return `${API_URL.replace(/^http/, 'ws')}/v1/messages/${conversationId}/socket?token=${encodeURIComponent(token)}`;
  }, [conversationId, getAccessToken]);

  const socket = useConversationSocket({
    conversationId,
    url: socketUrl,
    meId: user?.id ?? null,
    socketFactory,
  });

  const history = useChatMessages(conversationId, socket.shouldPoll);
  const send = useSendChatMessage(conversationId);
  const markRead = useMarkConversationRead();

  // Oznaka pročitano ide pri OTVARANJU razgovora, jednom po razgovoru.
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (markedRef.current === conversationId) return;
    markedRef.current = conversationId;
    markRead.mutate(conversationId);
    // `markRead` je stabilna mutacija; ovisnost o njoj bi vrtjela efekt u krug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const items = history.data?.items;
  useEffect(() => {
    // `scrollIntoView` ne postoji u jsdom-u i u starijim webviewima — nikad ne
    // smije srušiti razgovor zbog pomicanja prikaza.
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [items]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    try {
      await send.mutateAsync({ body, clientIdempotencyKey: newIdempotencyKey() });
    } catch (err) {
      setDraft(body);
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-label={partnerName}>
      <header className="flex items-center gap-3 border-b border-line-subtle pb-3">
        <Link
          to={listPath}
          aria-label={t('chat.backToList')}
          className="-ml-2 rounded-full p-2 text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink md:hidden"
        >
          <IconChevronLeft width={20} height={20} />
        </Link>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-[18px] font-semibold text-ink">{partnerName}</h2>
          <p className="text-[11.5px] text-ink-muted" data-testid="socket-status">
            {socket.partnerTyping
              ? t('chat.typing', { name: partnerName })
              : socket.status === 'open'
                ? t('chat.live')
                : socket.status === 'connecting' || socket.status === 'reconnecting'
                  ? t('chat.reconnecting')
                  : t('chat.polling')}
          </p>
        </div>
        <span
          aria-hidden="true"
          className={cx(
            'h-2 w-2 shrink-0 rounded-full',
            socket.status === 'open' ? 'bg-success' : 'bg-line-strong',
          )}
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        <QueryBoundary
          query={history}
          skeleton={<SkeletonList rows={4} />}
          isEmpty={(d) => d.items.length === 0}
          empty={{ message: t('chat.noMessages') }}
        >
          {(data) => (
            /*
              Pristigla poruka je jedina promjena na ekranu koju korisnik nije
              sam pokrenuo. `log` + `polite` najavljuje samo NOVE stavke, pa se
              povijest razgovora ne čita ispočetka pri svakoj poruci.
            */
            <ul role="log" aria-live="polite" aria-label={partnerName} className="flex flex-col gap-4">
              {data.items.map((m: MessageRow) => (
                <li key={m.id} className={cx(isOptimistic(m.id) && 'opacity-60')}>
                  <MessageBubble
                    body={m.body ?? ''}
                    from={m.mine ? 'me' : 'them'}
                    timeLabel={
                      isOptimistic(m.id)
                        ? t('chat.sending')
                        : `${formatTime(m.createdAt, locale)}${m.mine && m.readAt ? ` · ${t('chat.read')}` : ''}`
                    }
                    {...(m.mine ? {} : { authorName: partnerName })}
                  />
                </li>
              ))}
            </ul>
          )}
        </QueryBoundary>
        <div ref={bottomRef} />
      </div>

      {socket.status === 'unavailable' && (
        <p role="status" className="mb-2 rounded-md bg-bg-alt px-3 py-2 text-[12px] text-ink-muted">
          {t('chat.polling')}
        </p>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="flex gap-2 border-t border-line-subtle pt-3">
        <label htmlFor="chat-draft" className="sr-only">
          {t('chat.placeholder')}
        </label>
        <input
          id="chat-draft"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            socket.sendTyping();
          }}
          placeholder={t('chat.placeholder')}
          className="min-w-0 flex-1 rounded-pill border border-line bg-surface px-5 py-3 text-[15px] text-ink placeholder:text-ink-muted/70 focus:border-accent"
        />
        <Button type="submit" loading={send.isPending} disabled={!draft.trim()}>
          {t('chat.send')}
        </Button>
      </form>
    </section>
  );
}
